// AI Function Implementations
// Executes AI-triggered UI commands (no permission checks - all tools always allowed)

import { switchLanguage } from './language.js';

// ========== Color Contrast Helper Functions ==========
function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
}

function rgbToHex(rgbString) {
    const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return null;
    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);
    return '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');
}

function getLuminance(r, g, b) {
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;
    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

function isLightColor(color) {
    if (color.startsWith('rgb')) {
        color = rgbToHex(color);
        if (!color) return false;
    }
    const rgb = hexToRgb(color);
    const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
    return luminance > 0.5;
}

function getContrastingTextColor(backgroundColor) {
    return isLightColor(backgroundColor) ? '#121212' : '#ffffff';
}

// ========== AI Functions ==========

function changeLanguageByAI(languageCode) {
    if (typeof window.i18n !== 'undefined' && window.i18n.setLanguage) {
        const success = switchLanguage(languageCode);
        if (success) {
            console.log(`AI changed language to: ${languageCode}`);
        }
        return success;
    }
    return false;
}

function triggerConfetti() {
    const container = document.getElementById('confettiContainer');
    const currentThemeColor = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
    const colors = [currentThemeColor, currentThemeColor, '#FF4444', '#00FF66', '#0055AA', '#FFD700', '#FF69B4', '#9370DB'];
    const particleCount = 150;

    for (let i = 0; i < particleCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.width = (Math.random() * 10 + 5) + 'px';
        confetti.style.height = (Math.random() * 10 + 5) + 'px';
        confetti.style.animationDuration = (Math.random() * 3 + 2) + 's';
        confetti.style.animationDelay = (Math.random() * 0.5) + 's';
        container.appendChild(confetti);
        setTimeout(() => confetti.remove(), 5500);
    }

    console.log('AI triggered confetti effect');
    return true;
}

function flashScreen(color, duration = 500) {
    if (!color) {
        color = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
    }
    const flash = document.createElement('div');
    flash.className = 'screen-flash';
    flash.style.backgroundColor = color;
    flash.style.animationDuration = (duration / 1000) + 's';
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), duration);

    console.log(`AI triggered screen flash: ${color}`);
    return true;
}

function shakeScreen() {
    document.body.classList.add('shake');
    setTimeout(() => document.body.classList.remove('shake'), 500);

    console.log('AI triggered screen shake');
    return true;
}

function changeThemeColor(color) {
    document.documentElement.style.setProperty('--accent-color', color);

    const textColor = getContrastingTextColor(color);
    document.documentElement.style.setProperty('--user-message-text-color', textColor);

    const labelColor = isLightColor(color) ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 245, 230, 0.9)';
    document.documentElement.style.setProperty('--user-message-label-color', labelColor);

    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    document.documentElement.style.setProperty('--accent-rgb', `${r}, ${g}, ${b}`);

    const style = document.createElement('style');
    style.innerHTML = `
        #btn { border-color: ${color} !important; color: ${color} !important; box-shadow: 0 0 20px ${color}66 !important; }
        #settingsIcon { color: ${color} !important; }
        .modal-content { border-color: ${color} !important; }
        .modal-header h2 { color: ${color} !important; }
        .modal-header { border-bottom-color: ${color} !important; }
        .mini-modal-content { border-color: ${color} !important; }
    `;
    style.id = 'ai-theme-override';
    const oldStyle = document.getElementById('ai-theme-override');
    if (oldStyle) oldStyle.remove();
    document.head.appendChild(style);

    console.log(`AI changed theme color to: ${color}`);
    return true;
}

function showNotificationBanner(message, duration = 3000) {
    const banner = document.createElement('div');
    banner.className = 'notification-banner';
    banner.textContent = message;
    document.body.appendChild(banner);
    setTimeout(() => {
        banner.style.animation = 'slideDownBanner 0.5s ease-out reverse';
        setTimeout(() => banner.remove(), 500);
    }, duration);

    console.log(`AI showed notification: ${message}`);
    return true;
}

// Main AI Function Call Handler
export function handleAIFunctionCall(functionName, args = {}) {
    console.log(`AI Function Call: ${functionName}`, args);

    switch (functionName) {
        case 'change_language':
        case 'changeLanguage':
            return changeLanguageByAI(args.language || args.languageCode || args.lang);

        case 'trigger_confetti':
        case 'triggerConfetti':
        case 'confetti':
            return triggerConfetti();

        case 'flash_screen':
        case 'flashScreen':
            return flashScreen(args.color, args.duration);

        case 'shake_screen':
        case 'shakeScreen':
            return shakeScreen();

        case 'change_theme':
        case 'changeTheme':
            return changeThemeColor(args.color);

        case 'show_notification':
        case 'showNotification':
            return showNotificationBanner(args.message, args.duration);

        default:
            console.warn(`Unknown AI function: ${functionName}`);
            return false;
    }
}
